<template>
  <div>
    <p v-if="step.text">{{ getLocaleText(step.text) }}</p>
    <DisclaimerBox v-if="step.formStepType === 'personal_info'" id="disclaimerBox" />
    <div v-for="(input, index) in possibleInputs" :key="input.id">
      <DynamicFormInput
        v-if="showInput(input, formName)"
        class="dynamic-form-input"
        :class="{
          'is-even': (index + 1) % 2 === 0,
          'is-odd': (index + 1) % 2 !== 0,
          'mb-4': index < possibleInputs.length - 1
        }"
        :input-data="input"
        store="thematicCounselling"
        :form-name="formName"
        :gtm-form-name="gtmFormName"
        :step="step"
        :step-id="stepId"
        @update:value="() => valueUpdated(input)"
      />
    </div>

    <DynamicFormButtons
      v-if="showNavigationButton && !isDialog"
      :form-name="formName"
      :buttons="formButtons"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import dynamicForms from '@/mixins/dynamicForms';
export default {
  name: 'DynamicFormStep',
  mixins: [dynamicForms],
  props: {
    step: {
      type: Object,
      required: true
    },
    stepId: {
      type: Number,
      required: true
    },
    formName: {
      type: String,
      required: true
    },
    errorScrollContainer: {
      type: String,
      required: true
    },
    isLastStep: {
      type: Boolean,
      required: true
    },
    // TODO: upravit aby to bylo generické
    useOldGtm: {
      type: Boolean,
      default: false
    },
    gtmFormName: {
      type: String,
      required: false
    },
    isSending: {
      type: Boolean,
      required: true
    },
    isDialog: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapGetters({
      isEmergencyChecked: 'forms/emergencyDisclaimer/isEmergencyChecked'
    }),
    possibleInputs() {
      return this.step.formInputs.filter(input => {
        let inputUnderQuestion = false;
        if (input.dependencies.length) {
          inputUnderQuestion = !!input.dependencies.find(
            val => val.dependencyType !== 'value_only'
          );
        }
        return !inputUnderQuestion && this.showInput(input, this.formName);
      });
    },
    formButtons() {
      let buttons = [];

      if (this.showNavigationButton) {
        //show Prev Button
        if (
          (this.currentStep > 1 && !this.isDialog) ||
          this.currentStepData?.settings?.buttonsSettings?.prevButtonClickAction !== undefined
        ) {
          buttons.push({
            props: {
              color: 'tetrary'
            },
            events: {
              click: () => {
                this.$emit('prev-click');
              }
            },
            text: this.$t(
              this.currentStepData?.settings?.buttonsSettings?.prevButtonText || 'goPrev'
            )
          });
        }
      }

      buttons.push({
        type: 'sendButton',
        props: {
          loading: this.isSending,
          class: "{'transparent': isSending}",
          dataCy: 'dynamicFormStepSendButton'
        },
        events: {
          click: () => {
            if (!this.isEmergencyChecked) {
              document.getElementById('disclaimerBox')?.scrollIntoView({ behavior: 'smooth' });
            } else {
              this.nextButtonClick();
            }
          }
        },
        text: this.$t(
          this.currentStepData?.settings?.buttonsSettings?.nextButtonText ||
            (this.currentStep === this.possibleSteps.length ? 'send' : 'goNext')
        )
      });

      return buttons;
    }
  },
  watch: {
    currentStep() {
      this.dynamicFormSetup();
    },
    isSending() {
      this.dynamicFormSetup();
    }
  },
  mounted() {
    this.dynamicFormSetup();
  },
  methods: {
    resetStep() {
      this.step.formInputs.forEach(input => {
        this.$eventHub.$emit(`resetValidationInput_${input.id}_${this.stepId}`);
      });
    },

    valueUpdated(inputData) {
      if (!this.showNavigationButton) {
        this.$emit('next-click');
      }
      // TODO: upravit aby to bylo generické
      if (this.useOldGtm) {
        const formField =
          inputData.label?.find(lbl => lbl.language === 'cs')?.text ?? inputData.identifier;
        this.gtmPush({
          event: 'form_progress',
          'form.name': this.gtmFormName ?? this.formName,
          'form.field': formField
        });
      }
    },
    ...mapActions({
      updateCurrentStep: 'dynamicForms/updateCurrentStep',
      updateDialogButtons: 'dynamicForms/updateDialogButtons',
      updateDialogTitle: 'dynamicForms/updateDialogTitle',
      updateDialogLeftHeaderIcon: 'dynamicForms/updateDialogLeftHeaderIcon',
      updateHeaderType: 'dynamicForms/updateHeaderType'
    }),

    setStepButtons() {
      this.updateDialogButtons(this.formButtons);
    },

    setStepTitle() {
      if (
        (this.possibleSteps[this.currentStep - 1] &&
          (this.possibleSteps[this.currentStep - 1].settings.title ||
            this.possibleSteps[this.currentStep - 1].settings.showHeader)) ||
        this.$vuetify.breakpoint.smAndDown
      ) {
        this.updateDialogTitle(
          this.getLocaleText(this.possibleSteps[this.currentStep - 1].settings.title)
        );
      }
    },

    nextButtonClick() {
      try {
        if (this.currentStepData.nextAction === 'nextStep') {
          this.$emit('next-click');
          return;
        }
        this.currentStepData.settings.formInputs.forEach(input => {
          this.$eventHub.$emit(`validateInput_${input.id}_${this.currentStep - 1}`);
        });
      } catch (err) {
        this.handleError({ error: err, code: '0x00000011' });
      }
      this.$nextTick(() => {
        try {
          if (this.validateStep(this.currentStepData.settings.formInputs)) {
            this.$emit('next-click');
          } else {
            const scrollingBaseline = this.isHeaderScrolled ? -60 : -90;
            this.$nextTick(() =>
              this.scrollToError(
                scrollingBaseline - (!this.isDialog ? 70 : 0),
                this.errorScrollContainer
              )
            );
          }
        } catch (err) {
          this.handleError({ error: err, code: '0x00000012' });
        }
      });
    },
    dynamicFormSetup() {
      this.setStepButtons();
      this.setStepTitle();
      this.updateHeaderType('navigation');
      this.updateDialogLeftHeaderIcon('');
    }
  }
};
</script>
